import { useEffect, useState } from 'react';
import { Badge, Spinner, Stack } from 'react-bootstrap';
import { BsCheck } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import CheckmarkCell from '../../../../../components/tables/cellComponents/CheckmarkCell';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { PhoneNumber } from '../../../../common/PhoneNumber';
import { formatUserName, getCurrentUser } from '../../../../../services/helper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteChildMutation,
  useGetChildQuery
} from '../../../../../services/endpoints/people/child';
import { Caregiver, Child } from '../../../../../services/endpoints/people/user';
import { getErrorMessage } from '../../../../../utils/utils';

/** Merge Sequelize through-attrs onto caregiver for display (same as getUserProfile transform). */
const flattenCaregiver = (caregiver: Caregiver): Caregiver => {
  const { child_caregivers, ...rest } = caregiver;
  if (!child_caregivers) return caregiver;
  return { ...rest, ...child_caregivers };
};

export const ChildDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetChildQuery(+params.id!);
  const currentUser = getCurrentUser();
  const [child, setChild] = useState<Child>();
  const [deleteChild, { isLoading: isDeleting }] = useDeleteChildMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading child', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setChild(data.child);
    }
  }, [data]);

  if (currentUser?.isInstructor) {
    return <Forbidden />;
  }

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  if (!child) return <></>;

  const caregivers = (child.caregivers ?? []).map(flattenCaregiver);
  const legacyEmergencyContacts = (child.emergency_contacts ?? '').trim();

  const handleDelete = async () => {
    try {
      await deleteChild(Number(params.id!));
      addToast('Child deleted successfully', 'success');
      navigate(`/admin/sfc-users/children`);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => window.history.back()}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Children List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">
            {child?.first_name} {child?.last_name}
          </h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/sfc-users/children/${child.id}/edit`)}
          >
            Edit child
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete child
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="First Name:"
              text={child?.first_name}
            />
            <LabelText
              label="Last Name:"
              text={child?.last_name}
            />
            <LabelText
              label="Dob:"
              text={child?.dob}
            />
            <LabelText
              label="Dob formatted:"
              text={child?.dob_formatted ?? 'N/A'}
            />
            <LabelText
              label="Parent Guardian Notes:"
              text={child?.parent_guardian ?? 'N/A'}
            />
            <LabelText label="Parent Link:">
              {child.parent_id ? (
                <LinkCell
                  content={child.parent_guardian?.trim() || `Parent #${child.parent_id}`}
                  url={`/admin/sfc-users/parents/${child.parent_id}`}
                  underline
                />
              ) : (
                'N/A'
              )}
            </LabelText>
            <LabelText label="Emergency Contacts:">
              {caregivers.length > 0 ? (
                <Stack gap={2}>
                  {caregivers.map((caregiver, index) => (
                    <div key={caregiver.id ?? index}>
                      <div>
                        <b>{formatUserName(caregiver)}</b>
                        {caregiver.relation ? ` (${caregiver.relation})` : ''}
                      </div>
                      {caregiver.email ? <div>{caregiver.email}</div> : null}
                      {caregiver.phone ? (
                        <div>
                          <PhoneNumber number={caregiver.phone} />
                        </div>
                      ) : null}
                      <div className="text-muted">
                        {caregiver.pickup_allowed ? (
                          <Badge
                            pill
                            className="me-1"
                          >
                            Can Pickup <BsCheck />
                          </Badge>
                        ) : null}
                        {caregiver.is_emergency_contact ? (
                          <Badge pill>
                            Emergency Contact <BsCheck />
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </Stack>
              ) : (
                legacyEmergencyContacts || 'N/A'
              )}
            </LabelText>
            <LabelText
              label="Pickup Type:"
              text={child?.pickup_type ?? 'N/A'}
            />
            <LabelText
              label="Pickup Other:"
              text={child?.pickup_other ?? 'N/A'}
            />
            <LabelText
              label="Teacher/Classroom:"
              text={child?.origination ?? 'N/A'}
            />
            <LabelText
              label="Social Emotional Needs:"
              text={child?.social_emotional_needs}
            />
            <LabelText label="Allergies:">
              <Stack>
                {child?.allergies.map((a) => (
                  <LabelText label={a.allergy}>
                    <b>{a.severity}</b>&nbsp;
                    <span>{a.details}</span>
                  </LabelText>
                ))}
              </Stack>
            </LabelText>
            <LabelText label="Agree to terms:">
              <CheckmarkCell value={child?.agree_to_terms} />
            </LabelText>
          </DetailSection>
        </Stack>
      </div>

      <ConfirmationModal
        show={showModal}
        title="Delete Discount"
        message="Are you sure you want to remove this discount?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
